import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { useQuery } from "@tanstack/react-query";
import { fetchServerHistory, joinServer } from "../lib/api";
import { toast } from "sonner";
import PingIndicator from "./PingIndicator";
import MapThumbnail from "./MapThumbnail";
import RegionFlag from "./RegionFlag";
import DataAccumulatingNotice from "./DataAccumulatingNotice";
import { RichHistoryTooltip } from "./RichHistoryTooltip";
import { useFavorites } from "../context/FavoritesContext";
import { formatHours, formatNumber, timeAgo, lastSeenShort, formatDuration } from "../lib/format";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
  ReferenceArea,
} from "recharts";
import {
  Users,
  Globe,
  Map as MapIcon,
  Gauge,
  Lock,
  Shield,
  Clock,
  Copy,
  Play,
  Activity,
  Hash,
  Server as ServerIcon,
  Star,
  Share2,
} from "lucide-react";

const Pill = ({ icon, label, value, testId }) => (
  <div
    className="flex items-center gap-2 border px-3 py-2"
    style={{ borderColor: "var(--border-color)", background: "var(--bg-main)", borderRadius: 2 }}
    data-testid={testId}
  >
    <span style={{ color: "var(--primary-accent)" }}>{icon}</span>
    <div className="min-w-0">
      <div className="hud-label">{label}</div>
      <div className="font-mono-hud text-sm truncate" style={{ color: "var(--text-primary)" }}>{value}</div>
    </div>
  </div>
);

const ServerDetailModal = ({ server, open, onClose }) => {
  const { isFavorite, toggleFavorite } = useFavorites();
  const [bucket, setBucket] = React.useState(30);
  const [chartWindow, setChartWindow] = React.useState("1d");
  const [matchPage, setMatchPage] = React.useState(1);
  // Reset pager whenever the modal is opened for a different server.
  React.useEffect(() => { setMatchPage(1); }, [server?.id, open]);
  const { data: history, isLoading } = useQuery({
    queryKey: ["history", server?.id, bucket, chartWindow],
    queryFn: () => fetchServerHistory(server.id, bucket, chartWindow),
    enabled: open && !!server?.id,
    staleTime: 0,
  });

  if (!server) return null;

  const fav = isFavorite(server.id);

  const copyId = () => {
    navigator.clipboard.writeText(server.id);
    toast.success("Server ID copied", { description: server.id });
  };

  const copyConnect = () => {
    const addr = server.connect_addr || `${server.id.toLowerCase()}.wardogs.gg:27015`;
    const cmd = `connect ${addr}`;
    navigator.clipboard.writeText(cmd);
    toast.success("Connect command copied", { description: cmd });
  };

  const shareLink = () => {
    const url = `${window.location.origin}/server/${encodeURIComponent(server.id)}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied", { description: url });
  };

  const handleJoin = async () => {
    try {
      const res = await joinServer(server.id);
      const cmd = res.connect_command || "";
      // Wardogs live: wardogs:// deep link — attempt to launch the game client
      if (cmd.startsWith("wardogs://")) {
        try {
          window.location.href = cmd;
        } catch { /* ignore */ }
        navigator.clipboard.writeText(cmd);
        toast.success("Launching Wardogs...", {
          description: `${cmd} (copied to clipboard)`,
        });
      } else {
        navigator.clipboard.writeText(cmd);
        toast.success("Connect command copied", { description: cmd });
      }
    } catch {
      toast.error("Could not join server");
    }
  };

  const chartData = (history?.history || []).map((p) => ({
    t: p.t,
    time: new Date(p.t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    endTime: new Date(new Date(p.t).getTime() + (history?.bucket_minutes || 30) * 60000)
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    players: p.players,
    peak_players: p.peak_players ?? p.players,
    ping: p.ping,
    online: p.online !== false,
    online_pct: p.online_pct ?? (p.online !== false ? 100 : 0),
    map: p.map,
  }));

  // Contiguous ranges of offline hours → red ReferenceArea blocks.
  // Extend each range by half a bucket on both sides for better visibility.
  const offlineRanges = [];
  if (chartData.length) {
    let startIdx = null;
    for (let i = 0; i < chartData.length; i++) {
      const off = !chartData[i].online;
      const atEnd = i === chartData.length - 1;
      if (off && startIdx === null) startIdx = i;
      if (startIdx !== null && (!off || atEnd)) {
        const endIdx = off ? i : i - 1;
        const x1 = chartData[Math.max(0, startIdx - 1)].t;
        const x2 = chartData[Math.min(chartData.length - 1, endIdx + 1)].t;
        offlineRanges.push({ x1, x2 });
        startIdx = null;
      }
    }
  }
  const downtimeHours = chartData.filter((p) => !p.online).length;

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : null)}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        style={{
          background: "var(--bg-surface)",
          borderColor: "var(--primary-accent)",
          borderWidth: 1,
          borderRadius: 2,
        }}
        data-testid="server-detail-modal"
      >
        <div className="relative scanlines">
          <span className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2" style={{ borderColor: "var(--primary-accent)" }} />
          <span className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2" style={{ borderColor: "var(--primary-accent)" }} />

          <DialogHeader className="px-6 pt-6">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-1">
                <span
                  className="inline-block w-3 h-3 rounded-full"
                  style={{
                    background: server.online ? "var(--highlight)" : "#555",
                    boxShadow: server.online ? "0 0 10px var(--highlight)" : "none",
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="hud-caption mb-1 flex items-center gap-2 flex-wrap">
                  <span>{server.id}</span>
                  <span style={{ color: server.online ? undefined : "var(--ping-high)" }}>
                    · {server.online ? "ONLINE" : "OFFLINE"}
                  </span>
                  {!server.online && server.last_online_at && (
                    <span>· LAST SEEN {lastSeenShort(server.last_online_at)}</span>
                  )}
                  {server.region && (
                    <span className="inline-flex items-center gap-1.5" data-testid="modal-header-region">
                      ·
                      <RegionFlag region={server.region} size={14} testId="modal-header-region-flag" />
                      <span className="uppercase">{server.region}</span>
                    </span>
                  )}
                </div>
                <DialogTitle
                  className="font-head text-2xl md:text-3xl font-extrabold uppercase"
                  style={{ color: "var(--text-primary)", letterSpacing: "0.02em", lineHeight: 1.2 }}
                >
                  {server.name}
                </DialogTitle>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span
                    className="px-2 py-0.5 border font-mono-hud text-[10px] tracking-widest"
                    style={{
                      color: server.official ? "var(--official-color)" : "var(--primary-accent)",
                      borderColor: server.official ? "var(--official-color)" : "var(--primary-accent)",
                      background: "var(--bg-main)",
                    }}
                  >
                    {server.official ? "OFFICIAL" : "COMMUNITY"}
                  </span>
                  {server.password_protected && (
                    <span className="px-2 py-0.5 border font-mono-hud text-[10px] tracking-widest flex items-center gap-1" style={{ borderColor: "var(--secondary-accent)", color: "var(--secondary-accent)" }}>
                      <Lock className="w-3 h-3" /> PWD PROTECTED
                    </span>
                  )}
                  <span className="px-2 py-0.5 border font-mono-hud text-[10px] tracking-widest" style={{ borderColor: "var(--border-strong)", color: "var(--text-secondary)" }}>
                    AC: {server.anti_cheat}
                  </span>
                </div>
              </div>
              <PingIndicator ping={server.ping} online={server.online} server={server} showLabel />
            </div>
          </DialogHeader>

          <div className="px-6 py-5 space-y-5">
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{server.description}</p>

            {/* Map preview + modifier chips (Wardogs live only) */}
            {server.source === "wardogs" && (
              <div className="flex flex-col md:flex-row gap-4 items-start" data-testid="map-preview-block">
                <MapThumbnail
                  variant={server.map_variant || server.map}
                  base={server.map_base}
                  width={240}
                  height={135}
                />
                <div className="flex-1 min-w-0">
                  <div className="hud-label mb-2">MODIFIERS</div>
                  {server.modifiers && server.modifiers.length > 0 ? (
                    <div className="flex flex-wrap gap-2" data-testid="modifier-chips">
                      {server.modifiers.map((m) => (
                        <span
                          key={m}
                          className="font-mono-hud text-[11px] tracking-widest px-2 py-1 border"
                          style={{
                            color: "var(--primary-accent)",
                            borderColor: "color-mix(in srgb, var(--primary-accent) 50%, transparent)",
                            background: "color-mix(in srgb, var(--primary-accent) 12%, transparent)",
                            borderRadius: 2,
                          }}
                        >
                          {m}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="font-mono-hud text-sm" style={{ color: "var(--text-dim)" }}>
                      STANDARD · NO MODIFIERS
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Metadata grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="server-detail-pills">
              <Pill icon={<MapIcon className="w-4 h-4" />} label="MAP" value={server.map} />
              <Pill icon={<ServerIcon className="w-4 h-4" />} label="MODE" value={server.mode} />
              <Pill icon={<Globe className="w-4 h-4" />} label="REGION" value={server.region} />
              <Pill
                icon={<Users className="w-4 h-4" />}
                label="PLAYERS"
                value={`${server.players}/${server.max_players}`}
              />
              <Pill
                icon={<Clock className="w-4 h-4" />}
                label="UPTIME"
                value={formatHours(history?.uptime_hours ?? server.uptime_hours)}
              />
              <Pill icon={<Hash className="w-4 h-4" />} label="SERVER ID" value={server.id} />
            </div>

            {/* Wardogs map-rotation timings */}
            {server.source === "wardogs" && (history?.current_map_duration_min != null || history?.previous_map_duration_min != null) && (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-3"
                data-testid="map-rotation-timings"
              >
                <div
                  className="tac-card p-4"
                  style={{ background: "var(--bg-main)" }}
                >
                  <span className="corner-tr" /><span className="corner-bl" />
                  <div className="hud-label">PLAYING NOW · {server.map}</div>
                  <div
                    className="font-head text-2xl font-extrabold mt-1 tabular-nums"
                    style={{ color: "var(--primary-accent)" }}
                    data-testid="current-map-duration"
                  >
                    {history?.current_map_duration_min != null
                      ? formatDuration(history.current_map_duration_min)
                      : "—"}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                    In progress
                    {history?.current_map_started_at
                      ? ` · started at ${new Date(history.current_map_started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                      : ""}
                  </div>
                </div>
                <div
                  className="tac-card p-4"
                  style={{ background: "var(--bg-main)" }}
                >
                  <span className="corner-tr" /><span className="corner-bl" />
                  <div className="hud-label">PREVIOUS MAP · LASTED</div>
                  <div
                    className="font-head text-2xl font-extrabold mt-1 tabular-nums"
                    style={{ color: "var(--secondary-accent)" }}
                    data-testid="previous-map-duration"
                  >
                    {history?.previous_map_duration_min != null
                      ? formatDuration(history.previous_map_duration_min)
                      : "—"}
                  </div>
                  <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                    Before the current rotation flipped
                  </div>
                </div>
              </div>
            )}

            {/* Chart */}
            <div className="tac-card p-4" style={{ background: "var(--bg-main)" }}>
              <span className="corner-tr" />
              <span className="corner-bl" />
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="hud-label">PLAYER COUNT · {chartWindow === "1d" ? "LAST 24H" : chartWindow === "1w" ? "LAST 7 DAYS" : "LAST 30 DAYS"}</div>
                  <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
                    PEAK {history?.peak_players_24h ?? "—"} · AVG {history?.average_players_24h ?? "—"}
                    {downtimeHours > 0 && (
                      <span
                        className="ml-3 font-mono-hud text-xs tracking-widest px-2 py-0.5 border inline-block align-middle"
                        style={{
                          color: "var(--ping-high)",
                          borderColor: "color-mix(in srgb, var(--ping-high) 55%, transparent)",
                          background: "color-mix(in srgb, var(--ping-high) 10%, transparent)",
                        }}
                        data-testid="downtime-badge"
                      >
                        {downtimeHours}H DOWNTIME
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex items-center gap-1" data-testid="modal-window-toggle">
                    <span className="hud-label mr-1">WINDOW</span>
                    {[
                      { k: "1d", label: "1D" },
                      { k: "1w", label: "1W" },
                      { k: "1m", label: "1M" },
                    ].map((w) => (
                      <button
                        key={w.k}
                        type="button"
                        onClick={() => setChartWindow(w.k)}
                        data-testid={`modal-window-${w.k}`}
                        className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                        style={{
                          color: chartWindow === w.k ? "#000" : "var(--text-primary)",
                          background: chartWindow === w.k ? "var(--highlight)" : "var(--bg-elevated)",
                          borderColor: chartWindow === w.k ? "var(--highlight)" : "var(--border-strong)",
                          borderRadius: 2,
                        }}
                      >
                        {w.label}
                      </button>
                    ))}
                  </div>
                  {chartWindow === "1d" && (
                    <div className="flex items-center gap-1" data-testid="bucket-toggle">
                      <span className="hud-label mr-1">RESOLUTION</span>
                      {[10, 30, 60].map((b) => (
                        <button
                          key={b}
                          type="button"
                          onClick={() => setBucket(b)}
                          data-testid={`bucket-${b}`}
                          className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                          style={{
                            color: bucket === b ? "#000" : "var(--text-primary)",
                            background: bucket === b ? "var(--primary-accent)" : "var(--bg-elevated)",
                            borderColor: bucket === b ? "var(--primary-accent)" : "var(--border-strong)",
                            borderRadius: 2,
                          }}
                        >
                          {b < 60 ? `${b}M` : "1H"}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <DataAccumulatingNotice window={chartWindow} pointCount={history?.history?.length || 0} testId="modal-data-accumulating" />
              <div className="h-[220px]" data-testid="history-chart">
                {isLoading ? (
                  <div className="h-full flex items-center justify-center hud-caption">LOADING...</div>
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                      <defs>
                        <linearGradient id="playersGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--primary-accent)" stopOpacity={0.5} />
                          <stop offset="100%" stopColor="var(--primary-accent)" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="var(--border-color)" strokeDasharray="2 4" vertical={false} />
                      <XAxis
                        dataKey="t"
                        stroke="var(--text-dim)"
                        tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }}
                        tickLine={false}
                        axisLine={{ stroke: "var(--border-color)" }}
                        interval="preserveStartEnd"
                        minTickGap={28}
                        tickFormatter={(iso) =>
                          chartWindow === "1d"
                            ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                            : new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                        }
                      />
                      <YAxis
                        stroke="var(--text-dim)"
                        tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }}
                        tickLine={false}
                        axisLine={{ stroke: "var(--border-color)" }}
                        width={36}
                      />
                      <Tooltip
                        content={<RichHistoryTooltip bucket={history?.bucket_minutes || bucket} />}
                        cursor={{ stroke: "var(--primary-accent)", strokeOpacity: 0.4 }}
                      />
                      <Area
                        type="monotone"
                        dataKey="players"
                        stroke="var(--primary-accent)"
                        strokeWidth={2}
                        fill="url(#playersGrad)"
                      />
                      {offlineRanges.map((r, i) => (
                        <ReferenceArea
                          key={i}
                          x1={r.x1}
                          x2={r.x2}
                          strokeOpacity={0}
                          fill="var(--ping-high)"
                          fillOpacity={0.3}
                          ifOverflow="hidden"
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Recent matches */}
            <div className="tac-card p-4" style={{ background: "var(--bg-main)" }}>
              <span className="corner-tr" />
              <span className="corner-bl" />
              {(() => {
                const allMatches = history?.recent_matches || [];
                const perPage = 10;
                const totalPages = Math.max(1, Math.ceil(allMatches.length / perPage));
                const currentPage = Math.min(matchPage, totalPages);
                const pageSlice = allMatches.slice((currentPage - 1) * perPage, currentPage * perPage);
                const sliceOffset = (currentPage - 1) * perPage;
                return (
                  <>
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                      <div className="hud-label">
                        {server.source === "wardogs" ? "RECENT MAP CHANGES" : "RECENT MATCHES"} · UPTIME TIMELINE
                      </div>
                      {allMatches.length > 0 && (
                        <div className="hud-label" style={{ color: "var(--text-dim)" }}>
                          {allMatches.length} total
                        </div>
                      )}
                    </div>
                    <div className="divide-y" style={{ borderColor: "var(--border-color)" }} data-testid="recent-matches">
                      {isLoading && (
                        <div className="hud-caption py-4">LOADING MATCH DATA...</div>
                      )}
                      {!isLoading && allMatches.length === 0 && (
                        <div className="hud-caption py-4" style={{ color: "var(--text-dim)" }}>
                          NO RECENT EVENTS
                        </div>
                      )}
                      {!isLoading &&
                        pageSlice.map((m, i) => {
                          const absoluteIdx = sliceOffset + i;
                          const hasWinner = m.winner && m.winner !== "—" && m.winner !== "";
                          const hasScore = (m.score_a || 0) + (m.score_b || 0) > 0;
                          // The newest row (i === 0 on page 1) represents the map
                          // that is CURRENTLY in play — label it "Playing now"
                          // instead of "lasted" so it isn't mistaken for past.
                          const isCurrent = absoluteIdx === 0 && server.online && m.duration_min > 0;
                          return (
                            <div
                              key={absoluteIdx}
                              className="flex items-center justify-between py-2.5 border-t first:border-t-0"
                              style={{ borderColor: "var(--border-color)" }}
                              data-testid={`recent-match-row-${absoluteIdx}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="font-mono-hud text-xs" style={{ color: "var(--text-dim)" }}>
                                  {timeAgo(m.started_at)}
                                  {m.duration_min > 0 ? (
                                    isCurrent ? (
                                      <> · <span style={{ color: "var(--primary-accent)" }}>Playing now {formatDuration(m.duration_min)}</span></>
                                    ) : (
                                      <> · lasted {formatDuration(m.duration_min)}</>
                                    )
                                  ) : ""}
                                  {" · "}{m.map}
                                </div>
                                <div className="text-sm" style={{ color: "var(--text-primary)" }}>
                                  {m.mode}
                                  {hasWinner && (
                                    <> · <span style={{ color: "var(--highlight)" }}>{m.winner} WIN</span></>
                                  )}
                                </div>
                              </div>
                              {hasScore && (
                                <div className="font-mono-hud text-sm tabular-nums" style={{ color: "var(--text-primary)" }}>
                                  {m.score_a} <span style={{ color: "var(--text-dim)" }}>vs</span> {m.score_b}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                    {totalPages > 1 && (
                      <div
                        className="flex items-center justify-center gap-3 pt-3 mt-2 border-t"
                        style={{ borderColor: "var(--border-color)" }}
                        data-testid="recent-matches-pager"
                      >
                        <button
                          type="button"
                          onClick={() => setMatchPage((p) => Math.max(1, p - 1))}
                          disabled={currentPage <= 1}
                          data-testid="recent-matches-prev"
                          className="font-mono-hud text-xs tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{
                            color: "var(--text-primary)",
                            background: "var(--bg-elevated)",
                            borderColor: "var(--border-strong)",
                            borderRadius: 2,
                          }}
                        >
                          ← PREV
                        </button>
                        <span
                          className="font-mono-hud text-xs tabular-nums"
                          data-testid="recent-matches-page-label"
                          style={{ color: "var(--text-primary)" }}
                        >
                          PAGE <span style={{ color: "var(--primary-accent)" }}>{currentPage}</span> / {totalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setMatchPage((p) => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          data-testid="recent-matches-next"
                          className="font-mono-hud text-xs tracking-widest px-3 py-1.5 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                          style={{
                            color: "var(--text-primary)",
                            background: "var(--bg-elevated)",
                            borderColor: "var(--border-strong)",
                            borderRadius: 2,
                          }}
                        >
                          NEXT →
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Action bar */}
          <div
            className="sticky bottom-0 px-6 py-4 border-t flex flex-wrap gap-2"
            style={{ borderColor: "var(--border-color)", background: "color-mix(in srgb, var(--bg-surface) 92%, transparent)", backdropFilter: "blur(8px)" }}
          >
            <Button
              onClick={handleJoin}
              data-testid="server-join-btn"
              className="font-head tracking-widest font-bold"
              style={{
                background: "var(--primary-accent)",
                color: "#0a0a0a",
                borderRadius: 2,
              }}
            >
              <Play className="w-4 h-4 mr-2" /> JOIN SERVER
            </Button>
            <Button
              variant="outline"
              onClick={() => toggleFavorite(server)}
              data-testid="server-fav-btn"
              className="font-mono-hud tracking-widest"
              style={{
                background: fav ? "color-mix(in srgb, var(--secondary-accent) 20%, transparent)" : "var(--bg-elevated)",
                borderColor: fav ? "var(--secondary-accent)" : "var(--border-strong)",
                color: fav ? "var(--secondary-accent)" : "var(--text-primary)",
                borderRadius: 2,
              }}
            >
              <Star className="w-4 h-4 mr-2" style={{ fill: fav ? "var(--secondary-accent)" : "transparent" }} />
              {fav ? "FAVORITED" : "FAVORITE"}
            </Button>
            <Button
              variant="outline"
              onClick={shareLink}
              data-testid="server-share-btn"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}
            >
              <Share2 className="w-4 h-4 mr-2" /> SHARE
            </Button>
            <Button
              variant="outline"
              onClick={copyConnect}
              data-testid="server-copy-connect"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}
            >
              <Copy className="w-4 h-4 mr-2" /> CONNECT CMD
            </Button>
            <Button
              variant="outline"
              onClick={() => { window.location.href = `/server/${encodeURIComponent(server.id)}`; }}
              data-testid="server-full-page-btn"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--primary-accent)", color: "var(--primary-accent)", borderRadius: 2 }}
            >
              <Hash className="w-4 h-4 mr-2" /> OPEN FULL PAGE
            </Button>
            <Button
              variant="outline"
              onClick={copyId}
              data-testid="server-copy-id"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}
            >
              <Hash className="w-4 h-4 mr-2" /> COPY ID
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ServerDetailModal;
